/**
 * When a saved allocation change starts to bite.
 *
 * **Accounts are placed once a week: Wednesdays at 13:30 UTC.** Two Quartz jobs are
 * involved and it is easy to read the wrong one as "the allocation run", so both are
 * set out here. Crons live in `soenergy-charts/be-tickets/values.yaml`:
 *
 *   BE_TICKETS_DCA_ELIGIBILITY_JOB_CRON_EXPRESSION  "0 30 13 ? * WED *"  production
 *   BE_TICKETS_DCA_TICKET_JOB_CRON_EXPRESSION       "0 0 0/1 * * ?"      all envs
 *
 * `DcaEligibleAccountsJob` runs weekly. It pulls eligible accounts from BigQuery and
 * writes `dca_account_allocation` rows in an initial state - it makes no allocation
 * decision. `CreateDcaJuniferTicketsJob` runs hourly, and it is the one that reads
 * `dca_allocation_configuration_v2`, picks the agency and creates the Junifer ticket.
 *
 * The hourly job is therefore where the config is read, but it only has work to do on
 * rows the weekly job created. Between Wednesdays there is nothing to place, so it runs
 * ~160 times with nothing to allocate. The weekly job is the allocation run in every
 * sense a P&C user cares about, and the business case's "the automatic allocation on
 * 26 Aug and the one on 2 Sep" refers to it - both Wednesdays.
 *
 * The crons carry no timezone, so Quartz uses the JVM default, which is UTC in the
 * container. The instant is computed in UTC and rendered in the reader's own timezone -
 * telling someone 13:30 when their clock will say 14:30 is how a deadline gets missed.
 */

const WEDNESDAY_UTC = 3
const RUN_HOUR_UTC = 13
const RUN_MINUTE_UTC = 30

/**
 * The next weekly allocation run. A change saved after this week's run has started
 * cannot reach it, so it waits for the following week.
 */
export const nextAllocationRun = (now: Date = new Date()): Date => {
  const next = new Date(now)
  next.setUTCHours(RUN_HOUR_UTC, RUN_MINUTE_UTC, 0, 0)

  const daysAhead = (WEDNESDAY_UTC - next.getUTCDay() + 7) % 7
  next.setUTCDate(next.getUTCDate() + daysAhead)

  if (next <= now) {
    next.setUTCDate(next.getUTCDate() + 7)
  }

  return next
}

/**
 * Renders an instant in the reader's own timezone, naming it so there is no doubt -
 * "Wednesday 26 August at 14:30 BST".
 */
export const formatRun = (run: Date): string => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZoneName: "short",
  }).formatToParts(run)

  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((candidate) => candidate.type === type)?.value ?? ""

  return (
    `${part("weekday")} ${part("day")} ${part("month")} at ` +
    `${part("hour")}:${part("minute")} ${part("timeZoneName")}`
  )
}

/** The sentence shown on save. */
export const savedMessage = (what: string, now: Date = new Date()): string =>
  `${what} It applies from the next weekly DCA allocation run, ${formatRun(
    nextAllocationRun(now),
  )}.`
